import { useState } from 'react'
import { Button, Drawer, Input, Textarea, toast } from '@/components/ui'
import { useCreatePickup } from '../api/queries'

export interface NewPickupDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated?: (pickupId: string) => void
}

const EMPTY_FORM = {
  vendorName: '',
  vendorContactEmail: '',
  proposedPickupDate: '',
  pickupLocation: '',
  carrierName: '',
  notes: '',
}

/**
 * Manual entry point for pickups CARLA never staged (phone call, a triage
 * email that didn't parse, etc.) — mirrors
 * `logistics/components/NewQuoteDrawer.tsx`'s shape (Drawer, Cancel/Create
 * footer, `onCreated` callback the page wires to navigation).
 */
export function NewPickupDrawer({ open, onOpenChange, onCreated }: NewPickupDrawerProps) {
  const create = useCreatePickup()
  const [form, setForm] = useState(EMPTY_FORM)

  function reset() {
    setForm(EMPTY_FORM)
  }

  function field(key: keyof typeof EMPTY_FORM) {
    return {
      value: form[key],
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setForm((f) => ({ ...f, [key]: e.target.value })),
    }
  }

  function submit() {
    const vendorName = form.vendorName.trim()
    if (!vendorName) return
    create.mutate(
      {
        vendor_name: vendorName,
        vendor_contact_email: form.vendorContactEmail.trim() || undefined,
        proposed_pickup_date: form.proposedPickupDate || undefined,
        pickup_location: form.pickupLocation.trim() || undefined,
        carrier_name: form.carrierName.trim() || undefined,
        notes: form.notes.trim() || undefined,
      },
      {
        onSuccess: (pickup) => {
          toast.success(`Added pickup for ${vendorName}`)
          reset()
          onOpenChange(false)
          onCreated?.(pickup.id)
        },
        onError: (err) =>
          toast.danger(`Couldn't add pickup${err instanceof Error ? `: ${err.message}` : ''}`),
      },
    )
  }

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next) reset()
        onOpenChange(next)
      }}
      title="New pickup"
      meta="Add a pickup CARLA didn't stage — from a phone call or an email that didn't parse."
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => {
              reset()
              onOpenChange(false)
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={create.isPending}
            disabled={!form.vendorName.trim()}
            onClick={submit}
          >
            Create
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Vendor name</span>
          <Input placeholder="Vendor name" autoFocus {...field('vendorName')} />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Vendor contact email</span>
          <Input
            type="email"
            placeholder="contact@vendor.com"
            {...field('vendorContactEmail')}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Proposed pickup date</span>
          <Input type="date" {...field('proposedPickupDate')} />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Pickup location</span>
          <Input placeholder="e.g. Dock 3, 8am–noon" {...field('pickupLocation')} />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Carrier</span>
          <Input placeholder="Carrier name" {...field('carrierName')} />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Notes</span>
          <Textarea placeholder="Anything the team should know" {...field('notes')} />
        </label>
      </div>
    </Drawer>
  )
}
